/**
 * Onboarding tips overlay. Shows once per device (localStorage flag).
 * Originally scoped as a flying ghost-ship demo; reduced to a 3-card
 * tutorial overlay because the ghost-ship demo was unbounded scope.
 * The tips dismiss on first-pass, on Esc, or after 10 seconds.
 */

const KEY = "aether.onboarded";

const COARSE_HINTS = [
  { title: "Tilt or drag to steer", detail: "Use the touch pad in the bottom corner, or enable Tilt in settings." },
  { title: "Pass rings to extend your drift", detail: "Hit them and time grows. Miss and time burns." },
  { title: "Hold the pad to boost", detail: "Boost activates motion effects, music, and god-rays." },
];

const FINE_HINTS = [
  { title: "W A S D or arrows to steer", detail: "Pointer also works — click and drag." },
  { title: "Space to boost", detail: "Boost activates motion effects, adaptive music, and god-rays." },
  { title: "Pass rings to extend your drift", detail: "Hit them and time grows. Miss and time burns." },
];

export function maybeShowOnboarding(): { dismiss: () => void } | null {
  try {
    if (localStorage.getItem(KEY)) return null;
  } catch {
    return null;
  }
  const coarse = matchMedia("(pointer: coarse)").matches;
  const hints = coarse ? COARSE_HINTS : FINE_HINTS;

  const host = document.createElement("div");
  host.className = "onboarding-overlay";
  host.innerHTML = `
    <div class="onboarding-card">
      <ol class="onboarding-list">
        ${hints.map((h) => `<li><strong>${escapeHtml(h.title)}</strong><span>${escapeHtml(h.detail)}</span></li>`).join("")}
      </ol>
      <button class="onboarding-dismiss" data-action="onboard-close">Got it</button>
    </div>
  `;
  document.body.appendChild(host);
  requestAnimationFrame(() => host.classList.add("in"));

  // Focus-trap so keyboard users can't tab out onto the canvas and
  // strand themselves with no visible focus indicator. Esc dismisses.
  let trap: import("./focus-trap").FocusTrap | null = null;

  let dismissed = false;
  const dismiss = () => {
    if (dismissed) return;
    dismissed = true;
    try { localStorage.setItem(KEY, "1"); } catch {}
    host.classList.remove("in");
    host.classList.add("out");
    trap?.deactivate();
    setTimeout(() => host.remove(), 280);
  };

  // Lazy-require to avoid a top-level import (this module already
  // ships in the cold-load path; the trap is only used here once).
  import("./focus-trap").then((m) => {
    trap = new m.FocusTrap();
    trap.activate(host, dismiss);
  });

  host.querySelector<HTMLButtonElement>('[data-action="onboard-close"]')!.addEventListener("click", dismiss);
  // Auto-dismiss after 10 seconds, in case the player just starts playing.
  const autoTimer = window.setTimeout(dismiss, 10000);

  return {
    dismiss: () => {
      window.clearTimeout(autoTimer);
      dismiss();
    },
  };
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}
